import { ErrorSeverity } from '../enums/ErrorSeverity';
import { ValidationError } from '../services/ValidationError';
import ServerError from '../services/ServerError';
import NetworkError from '../services/NetworkError';
import { setErrorMessage } from '../redux/slices/errors';
import { setNotification } from '../redux/slices/notifications';
import { setComponentError } from '../components/ComponentError/redux';
import { getUserFriendlyErrorMessage, reportError } from './errorHelpers';

const defaultCancelResult = { wasSuccessful: false, wasCancelled: true };
const defaultErrorResult = { wasSuccessful: false };

/**
 * Determines whether an error should be reported to Sentry/AppInsights based on severity and error type
 * @param {number} effectiveSeverity - The effective error severity
 * @param {boolean} isValidationError - Whether the error is a ValidationError
 * @returns {boolean} True if the error should be reported
 */
function getShouldReportError(effectiveSeverity, isValidationError) {
    // Skip ValidationErrors as they are handled by form flow and shouldn't be reported
    if (isValidationError) return false;

    // Skip NONE severity as these are intentionally silent
    if (effectiveSeverity === ErrorSeverity.NONE) return false;

    // Skip CRITICAL severity as ErrorDialog component reports these errors itself
    if (effectiveSeverity === ErrorSeverity.CRITICAL) return false;

    // Report ERROR, WARNING, and INFO severities
    return (
        effectiveSeverity === ErrorSeverity.ERROR ||
        effectiveSeverity === ErrorSeverity.WARNING ||
        effectiveSeverity === ErrorSeverity.INFO
    );
}

/**
 * Handles API call failures with comprehensive error reporting and user feedback
 * @param {Error} err - The error that occurred
 * @param {Object} options - Configuration options
 * @param {Function} options.dispatch - Redux dispatch function
 * @param {string} options.errorMessage - Custom error message
 * @param {boolean} options.useUserFriendlyErrors - Whether to use user-friendly error messages
 * @param {number} options.effectiveSeverity - The effective error severity
 * @param {string} options.componentId - Component ID for component-specific errors
 * @param {boolean} options.shouldAutoDismissNotification - Whether notifications should auto-dismiss
 * @param {boolean} options.mapFormErrors - Whether form errors are being mapped
 * @param {*} options.result - The current result object
 * @returns {Object} The result object or default error/cancel result
 */
export async function handleApiFailure(err, options) {
    const {
        dispatch,
        errorMessage,
        useUserFriendlyErrors,
        effectiveSeverity,
        componentId,
        shouldAutoDismissNotification,
        mapFormErrors,
        result,
    } = options;

    // if the call was aborted, or the call was unauthorized (thus redirected to logout)
    // or the error is a network error (lost api / internet connection)
    // don't handle it like can error, just return the default cancel result
    if (err.name === 'AbortError' || (err.name === 'ServerError' && err?.status === 401))
        return defaultCancelResult;

    // Extract error code from response JSON if available
    let errorCode = null;
    if (err.responseJson?.errorCode) {
        errorCode = err.responseJson.errorCode;
    }

    // Note: No need to call err.json() again here as ApiService already calls it
    // log to console unless a ServerError or NetworkError as they are already logged to console
    if (!(err instanceof ServerError) && !(err instanceof NetworkError)) console.error('error occurred in handleApiCall():', err);

    // Use custom error message if provided, otherwise use friendly/technical error message
    let fullErrorMessage;
    if (errorMessage) {
        // If custom error message is provided, use it as the primary message
        fullErrorMessage = errorMessage;
    } else {
        // Fall back to user-friendly or technical error message
        fullErrorMessage = useUserFriendlyErrors
            ? getUserFriendlyErrorMessage(err)
            : err.message;
    }

    // Add error code to error message if available (for 500 errors and other server errors)
    if (errorCode) {
        fullErrorMessage = `${fullErrorMessage} (Error Code: ${errorCode})`;
    }

    const isValidationError = err instanceof ValidationError;
    const shouldReportError = getShouldReportError(effectiveSeverity, isValidationError);

    // Report errors to Sentry/AppInsights based on severity and error type
    if (shouldReportError) {
        try {
            // Create error object with proper context for reporting
            // The reportError function will handle error normalization and extract technical details
            // NOTE: We pass the original error for technical reporting, not the user-friendly message
            const errorForReporting = {
                errorObject: err, // Original technical error for Sentry
                userFriendlyMessage: fullErrorMessage, // User-friendly message for context
                tags: {
                    severity: ErrorSeverity.getDescription(effectiveSeverity),
                    componentId: componentId || null,
                },
            };

            // Report to Sentry/AppInsights - this handles normalization, deduplication and proper formatting
            const reportedErrorCode = reportError(errorForReporting);

            // Use the reported error code if we don't already have one
            if (!errorCode && reportedErrorCode) {
                errorCode = reportedErrorCode;
                // Update the error message with the generated error code
                fullErrorMessage = `${fullErrorMessage} (Error Code: ${errorCode})`;
            }
        } catch (reportingError) {
            // Don't let error reporting failure affect the main error handling flow
            console.warn('Failed to report error to Sentry/AppInsights:', reportingError);
        }
    }

    // Handle component-level errors if componentId is provided
    if (componentId) {
        await dispatch(
            setComponentError({
                componentId,
                description: fullErrorMessage,
                errorObject: err,
            }),
        );
        
        // If this is a component error for form validation that was already mapped,
        // we're done - don't show additional notifications
        if (isValidationError && err.isComponentError) {
            return result || defaultErrorResult;
        }
    }

    // Handle based on error severity
    switch (effectiveSeverity) {
        case ErrorSeverity.CRITICAL:
            await dispatch(
                setErrorMessage({ description: fullErrorMessage, errorObject: err }),
            );
            break;
        case ErrorSeverity.ERROR:
            await dispatch(
                setNotification({
                    message: fullErrorMessage,
                    errorCode: errorCode,
                    options: {
                        appearance: 'error',
                        autoDismiss: shouldAutoDismissNotification,
                    },
                }),
            );
            break;

        case ErrorSeverity.WARNING:
            await dispatch(
                setNotification({
                    message: fullErrorMessage,
                    options: {
                        appearance: 'warning',
                        autoDismiss: shouldAutoDismissNotification,
                    },
                }),
            );
            break;

        case ErrorSeverity.INFO:
            await dispatch(
                setNotification({
                    message: fullErrorMessage,
                    options: {
                        appearance: 'info',
                        autoDismiss: shouldAutoDismissNotification,
                    },
                }),
            );
            break;

        case ErrorSeverity.NONE:
            // Silent error - only logged to console (already done above)
            break;
        default:
            // This should not happen with the new system, but provide a sensible fallback
            console.warn(
                `Unknown errorSeverity: ${effectiveSeverity}. Falling back to error toast.`,
            );
            await dispatch(
                setNotification({
                    message: fullErrorMessage,
                    errorCode: errorCode,
                    options: {
                        appearance: 'error',
                        autoDismiss: shouldAutoDismissNotification,
                    },
                }),
            );
            break;
    }

    return result || defaultErrorResult;
}
